import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Button } from '~/components/Button';
import { useUser } from '~/modules/auth/hooks/useUser';
import { DetailPageError } from '~/modules/errors/components/DetailPageError';
import type { LabelGroupDetail } from '~settings/api/lib/schemas';
import { useLabelGroupDetails } from '~settings/api/queries/labelGroup';
import { SettingsPageHeader } from '~settings/components/SettingsPageHeader';
import { SETTINGS_ROUTES } from '~settings/config/routes';
import { LabelGroupForm } from '~settings/modules/label-group/components/LabelGroupForm';
import { toDropdownList } from '~settings/utils/transformers';

export function DetailLabelGroupPage() {
  const { t } = useTranslation('settings');
  const { hasPermission } = useUser();

  const { labelGroupId = '' } = useParams();
  const canEditLabelGroup = hasPermission('LABEL_GROUP.EDIT');

  const { isLoading, error, labelGroup } = useLabelGroupDetails(
    { labelGroupId },
    {
      select: (values: LabelGroupDetail) => ({
        ...values,
        notes: values.notes ?? '',
        labels: toDropdownList(values.labels) ?? [],
        users: toDropdownList(values.users ?? [], 'fullName'),
      }),
    },
  );

  if (isLoading) {
    return <section className="py-md">{t('loading')}</section>;
  }

  if (error || !labelGroup) {
    return (
      <DetailPageError
        errorCode={error?.response?.status}
        fallbackRoute={{
          href: SETTINGS_ROUTES.LABEL_GROUP_LIST,
          label: t('backToUserManagement'),
        }}
      />
    );
  }

  return (
    <article className="relative flex h-full flex-col">
      <SettingsPageHeader
        breadcrumbItems={[
          { to: SETTINGS_ROUTES.ROOT, label: 'title' },
          { to: SETTINGS_ROUTES.LABEL_GROUP_LIST, label: 'labelGroup.list' },
          { to: '', label: labelGroup.name },
        ]}
        title={labelGroup.name}
        actions={
          canEditLabelGroup ? (
            <Button
              asChild
              variant="secondary"
              className="shrink-0 self-center"
              size="large"
            >
              <Link to={SETTINGS_ROUTES.LABEL_GROUP_EDIT(labelGroupId)}>
                {t('labelGroup.editLink')}
              </Link>
            </Button>
          ) : null
        }
      />

      <div className="grow overflow-y-auto pb-xl">
        <LabelGroupForm onSubmit={() => false} values={labelGroup} readOnly />
      </div>
    </article>
  );
}

DetailLabelGroupPage.displayName = 'DetailLabelGroupPage';
